import { useState, useEffect } from "react";
import { useOrganization } from "@clerk/clerk-react";
import { apiRequest } from "@/lib/queryClient";

export interface OrgMetadata {
  type?: "customer" | "profit-center";
  hajocaCustomerId?: string;
  hajocaProfitCenterId?: string;
  phone?: string;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
}

interface UseOrgMetadataResult {
  metadata: OrgMetadata | null;
  isLoading: boolean;
  error: string | null;
  updateMetadata: (newMetadata: Partial<OrgMetadata>) => Promise<void>;
  getMetadataValue: <K extends keyof OrgMetadata>(
    key: K,
  ) => OrgMetadata[K] | null;
  isLoaded: boolean;
}

export const useOrgMetadata = (): UseOrgMetadataResult => {
  const { organization, isLoaded } = useOrganization();
  const [metadata, setMetadata] = useState<OrgMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load metadata from organization object
  useEffect(() => {
    if (isLoaded && organization) {
      const orgMetadata = organization.publicMetadata as OrgMetadata;
      setMetadata(orgMetadata || {});
    }
  }, [isLoaded, organization]);

  const updateMetadata = async (newMetadata: Partial<OrgMetadata>) => {
    if (!organization) {
      throw new Error("No organization found");
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiRequest(
        "PATCH",
        `/api/orgs/${organization.id}/metadata`,
        {
          publicMetadata: newMetadata,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update metadata");
      }

      // Reload organization to get fresh metadata
      await organization.reload();

      // Update local state
      const orgMetadata = organization.publicMetadata as OrgMetadata;
      setMetadata(orgMetadata || {});
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getMetadataValue = <K extends keyof OrgMetadata>(
    key: K,
  ): OrgMetadata[K] | null => {
    if (!metadata) return null;
    return metadata[key] || null;
  };

  return {
    metadata,
    isLoading,
    error,
    updateMetadata,
    getMetadataValue,
    isLoaded: isLoaded && metadata !== null,
  };
};
